import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { t } from '../../../i18n/i18n';
import './Overview.scss';

/**
 * Placeholders for the Competition Overview's top section while
 * `GET /v2/competition/detail/` is in flight. One call answers both cards —
 * the trader's figures are nested in the competition record — so they wait on
 * it together rather than one landing ahead of the other.
 *
 * It renders the page's own layout classes — `co-top`, `cnc`, `scs` — so both
 * cards hold their 220px height and the two-column split at every breakpoint,
 * and the loaded content drops into the same boxes. The hero sits on the blue
 * gradient, where 6% white is invisible, so its bars are `light`.
 *
 * The leaderboard needs nothing here: `DataTable` already draws skeleton rows
 * under its real header when `LeaderboardTable` passes `loading`.
 */

const range = (n) => Array.from({ length: n }, (_, i) => i);

/** Rank · Profit · Trades · Win rate · Drawdown left — widths follow the real
 *  labels and figures, which is what makes the row read as the one it replaces. */
const CELLS = [
  { label: 34, value: 58, note: 116 },
  { label: 38, value: 78, note: 92 },
  { label: 42, value: 32, note: 84 },
  { label: 52, value: 44, note: 74 },
  { label: 82, value: 70, note: 98 },
];

/** Gross profit · Gross loss · Best trade · Worst trade. */
const INLINE = [
  { label: 62, value: 56 },
  { label: 54, value: 60 },
  { label: 54, value: 52 },
  { label: 62, value: 48 },
];

export function TopSkeleton() {
  return (
    <section className="co-top" aria-busy="true" aria-label={t('Loading your current competition')}>
      {/* ---- Competing now — hero card ---- */}
      <section className="cnc">
        <div className="cnc__intro">
          <Skeleton w="86px" h={10} light />
          <Skeleton w="min(232px, 72%)" h={26} light />
          <Skeleton w="min(288px, 92%)" h={11} light />
        </div>

        <div className="cnc__countdown">
          <div className="cnc__timer">
            <Skeleton w="48px" h={10} light />
            <div className="cnc__units">
              {range(4).map((i) => (
                <div className="cnc__unit" key={i}>
                  <Skeleton w="36px" h={26} light />
                  <Skeleton w="7px" h={10} light />
                </div>
              ))}
            </div>
          </div>

          <Skeleton w="128px" h={11} light />
        </div>

        {/* The rail is already its own track, and only the fill is unknown —
            a bar inside it would pulse at 20% white over 18% white. */}
        <span className="cnc__rail" />
      </section>

      {/* ---- Stats summary card ---- */}
      <section className="scs">
        <div className="scs__flex">
          {CELLS.map((cell, i) => (
            <div className="scs__cell" key={i}>
              <Skeleton w={cell.label} h={11} />
              <Skeleton w={cell.value} h={20} />
              <Skeleton w={cell.note} h={10} />
            </div>
          ))}
        </div>

        <div className="scs__inline">
          {INLINE.map((item, i) => (
            <div className="scs__inline-item" key={i}>
              <Skeleton w={item.label} h={11} />
              <Skeleton w={item.value} h={12} />
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}
